import type { ReactNode } from 'react';
import React from 'react';

import { Typography } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { useIntercom } from 'react-use-intercom';
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineErrorIcon } from 'src/components/icons/AlineErrorIcon';
import { themeV5 } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return <ErrorBoundary fallback={() => <AppErrorFallbackContent />}>{children}</ErrorBoundary>;
}

const useStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },

  content: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: 'auto',
    maxWidth: 520,
    textAlign: 'center',
  },

  iconCircle: {
    backgroundColor: 'white',
    border: '1px solid',
    borderColor: theme.palette.grey[200],
    borderRadius: '50%',
    padding: 16,
  },

  root: {
    background: theme.appColors.defaultFieldBackground,
    display: 'flex',
    inset: 0,
    minHeight: '100vh',
    padding: theme.spacing(1),
    position: 'fixed',
  },
}));

function AppErrorFallbackContent() {
  const { classes } = useStyles();
  const { show: showIntercom } = useIntercom();

  function handleRefresh() {
    window.location.reload();
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.iconCircle}>
          <AlineErrorIcon fill={themeV5.palette.error.light} height={48} width={48} />
        </div>

        <Typography variant="h1">An error occurred</Typography>
        <Typography>Something went wrong. Refresh the page or contact support.</Typography>

        <div className={classes.actions}>
          <AlineButton color="primary" variant="outlined" onClick={showIntercom}>
            Contact support
          </AlineButton>
          <AlineButton color="primary" variant="contained" onClick={handleRefresh}>
            Refresh
          </AlineButton>
        </div>
      </div>
    </div>
  );
}
